import React, { useEffect } from 'react';
import classnames from 'classnames';

import { DoNotCallListPhoneNumber } from '../../../../../../types/do-not-call-list';
import { accessibleOnClick } from '../../../../../../../../shared/utils/accessible-on-click';
import Alert from '../../../../../../../../shared/design-system/ui/alert';

type BulkSelectedPhoneNumbers = {
  isAllPhoneNumbersSelected?: boolean;
  deSelectedPhoneNumberIds?: any[];
};

type IProps = {
  selectedPhoneNumbers: DoNotCallListPhoneNumber[];
  bulkSelectedPhoneNumbers: BulkSelectedPhoneNumbers;
  totalPhoneNumbers: number;
  handleSelectAllRows: VoidFunction;
  handleClearSelection: VoidFunction;
};

const getSelectedCount = (
  selected: DoNotCallListPhoneNumber[],
  bulk: BulkSelectedPhoneNumbers,
  total: number,
): number => {
  if (bulk?.isAllPhoneNumbersSelected) {
    return total - (bulk.deSelectedPhoneNumberIds?.length || 0);
  }
  return selected?.length || 0;
};

const SelectedPhoneNumbersInfoBanner: React.FC<IProps> = ({
  selectedPhoneNumbers,
  bulkSelectedPhoneNumbers,
  totalPhoneNumbers,
  handleSelectAllRows,
  handleClearSelection,
}) => {
  const selectedCount = getSelectedCount(
    selectedPhoneNumbers,
    bulkSelectedPhoneNumbers,
    totalPhoneNumbers,
  );

  useEffect(() => {
    if (selectedCount === 0) {
      handleClearSelection();
    }
  }, [selectedCount]);

  const divClasses = classnames([
    'email-selected-row-info alert-width',
    {
      'email-selected-row-info-waring':
        selectedPhoneNumbers.length > 0 ||
        bulkSelectedPhoneNumbers?.isAllPhoneNumbersSelected,
    },
    {
      'email-selected-row-info-danger':
        selectedPhoneNumbers.length === 0 &&
        !bulkSelectedPhoneNumbers?.isAllPhoneNumbersSelected,
    },
  ]);

  const spanClasses = classnames([
    'semibold-1',
    {
      'popover-arrow-color-txt':
        selectedPhoneNumbers.length > 0 ||
        bulkSelectedPhoneNumbers?.isAllPhoneNumbersSelected,
    },
    {
      'red-txt-12':
        selectedPhoneNumbers.length === 0 &&
        !bulkSelectedPhoneNumbers?.isAllPhoneNumbersSelected,
    },
  ]);

  const renderContent = () => (
    <div className="d-flex">
      <span className={`${spanClasses} mr-2`}>
        {selectedCount} selected
      </span>

      {!bulkSelectedPhoneNumbers?.isAllPhoneNumbersSelected &&
        totalPhoneNumbers !== selectedPhoneNumbers?.length && (
          <>
            <span
              role="button"
              className="semibold-1 blue-txt-11"
              {...accessibleOnClick(handleSelectAllRows)}
            >
              Select all entries
            </span>
            <span className="divider-vertical" />
          </>
        )}

      <span
        role="button"
        className="semibold-1 blue-txt-11"
        {...accessibleOnClick(handleClearSelection)}
      >
        Clear selection
      </span>
    </div>
  );

  if (totalPhoneNumbers > 0) {
    return (
      <Alert
        className={divClasses}
        variant="alert"
        description={renderContent()}
      />
    );
  }

  return <></>;
};

export default SelectedPhoneNumbersInfoBanner;
